import { ArrowDown, ArrowUpRight, Blocks, Box, Gamepad2, Mail, MountainSnow } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PUBLIC_CONTACT_EMAIL as contactEmail } from '../config/publicSite';

const exilSteamUrl = 'https://store.steampowered.com/app/4131390/Exil_Island/';

export function HomePage() {
  const { t } = useTranslation();

  return (
    <div className="overflow-hidden">
      <section className="relative border-b border-dark-700" aria-labelledby="landing-title">
        <div aria-hidden="true" className="pointer-events-none absolute -right-32 -top-48 h-[640px] w-[640px] rounded-full bg-neon-500/[0.06] blur-[100px]" />
        <div className="relative mx-auto grid max-w-6xl gap-14 px-6 py-16 sm:py-24 lg:grid-cols-[1.2fr_1fr] lg:items-center lg:gap-16 lg:py-20">
          <div>
            <p className="mb-8 inline-flex items-center gap-2 rounded-full border border-neon-500/25 bg-neon-500/5 px-3 py-1.5 text-xs font-medium text-neon-300">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-neon-400" />
              {t('landing.badge')}
            </p>
            <h1 id="landing-title" className="text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
              {t('landing.title')}<br />
              <span className="text-neon-400">{t('landing.highlight')}</span>
            </h1>
            <p className="mt-7 max-w-lg text-lg leading-relaxed text-gray-400">{t('landing.description')}</p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <a href={`mailto:${contactEmail}`} className="btn-primary px-5 py-3">
                <Mail className="h-4 w-4" aria-hidden="true" />
                {t('landing.contact')}
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </a>
              <a href="#games" className="inline-flex items-center gap-2 py-3 text-sm text-gray-300 transition-colors hover:text-neon-400 focus-visible:outline focus-visible:outline-neon-400">
                {t('landing.explore')}
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              </a>
            </div>
            <p className="mt-7 max-w-lg text-sm leading-relaxed text-gray-500">{t('landing.notice')}</p>
          </div>

          <div className="relative">
            <div aria-hidden="true" className="pointer-events-none absolute -inset-4 rounded-[2rem] border border-dark-700/60 sm:-inset-5" />
            <div className="relative overflow-hidden rounded-2xl border border-dark-600 bg-dark-900 shadow-2xl">
              <div className="flex items-center justify-between border-b border-dark-700 px-5 py-4">
                <span className="font-mono text-xs tracking-widest text-gray-400">HOSTINATOR / HOSTING</span>
                <div aria-hidden="true" className="flex gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-dark-400" /><span className="h-1.5 w-1.5 rounded-full bg-dark-400" /><span className="h-1.5 w-1.5 rounded-full bg-neon-400" /></div>
              </div>
              <div className="p-5 sm:p-7">
                <p className="mb-4 text-xs font-medium uppercase tracking-[0.18em] text-neon-400">{t('landing.visualLabel')}</p>
                <div className="space-y-3">
                  {[
                    { name: 'Minecraft', detail: t('landing.minecraftTag'), icon: Blocks, color: 'text-neon-400 bg-neon-500/10 border-neon-500/20' },
                    { name: 'Valheim', detail: t('landing.valheimTag'), icon: MountainSnow, color: 'text-cyber-400 bg-cyber-500/10 border-cyber-500/20' },
                  ].map(({ name, detail, icon: Icon, color }) => (
                    <div key={name} className="flex items-center gap-4 rounded-xl border border-dark-600 bg-dark-800/80 p-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border ${color}`}><Icon className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" /></div>
                      <div className="min-w-0"><p className="font-semibold text-gray-100">{name}</p><p className="mt-1 text-xs leading-relaxed text-gray-400">{detail}</p></div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm font-medium text-gray-300"><span className="mr-2 text-neon-400">+</span>{t('landing.more')}</p>
                <div className="mt-6 border-t border-dark-600 pt-5">
                  <p className="mb-3 text-xs font-medium uppercase tracking-[0.15em] text-violet-400">{t('landing.customTitle')}</p>
                  <a href={exilSteamUrl} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 rounded-xl border border-violet-500/20 bg-violet-500/5 p-4 transition-colors hover:border-violet-400/50 hover:bg-violet-500/10 focus-visible:outline focus-visible:outline-violet-400">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-violet-500/20 bg-violet-500/10 text-violet-400"><Box className="h-6 w-6" strokeWidth={1.5} aria-hidden="true" /></div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 font-semibold text-gray-100">Exil Island<ArrowUpRight className="h-4 w-4 text-violet-400" aria-hidden="true" /></p>
                      <p className="mt-1 text-xs text-gray-400">{t('landing.exilTag')}</p>
                      <p className="mt-2 text-xs font-medium text-violet-300 group-hover:text-violet-200">{t('landing.wishlist')}</p>
                    </div>
                  </a>
                </div>
              </div>
              <div className="border-t border-dark-700 bg-dark-800/40 px-5 py-4 font-mono text-[11px] tracking-wider text-gray-500 sm:px-7">{t('landing.visualFooter')}</div>
            </div>
          </div>
        </div>
      </section>

      <section id="games" className="mx-auto max-w-6xl scroll-mt-24 px-6 py-16 sm:py-20" aria-labelledby="games-title">
        <div className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-neon-400">{t('landing.gamesEyebrow')}</p>
            <h2 id="games-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('landing.gamesTitle')}</h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-gray-400">{t('landing.gamesIntro')}</p>
        </div>
        <div className="grid gap-8 border-y border-dark-700 py-8 md:grid-cols-3 md:gap-10">
          {[
            { name: 'Minecraft', description: t('landing.minecraftDescription'), icon: Blocks, color: 'text-neon-400' },
            { name: 'Valheim', description: t('landing.valheimDescription'), icon: MountainSnow, color: 'text-cyber-400' },
            { name: t('landing.moreGamesTitle'), description: t('landing.moreGamesDescription'), icon: Gamepad2, color: 'text-neon-400' },
          ].map(({ name, description, icon: Icon, color }) => (
            <article key={name}>
              <Icon className={`mb-5 h-7 w-7 ${color}`} strokeWidth={1.5} aria-hidden="true" />
              <h3 className="mb-3 text-lg font-semibold">{name}</h3>
              <p className="text-sm leading-7 text-gray-400">{description}</p>
            </article>
          ))}
        </div>
        <div className="mt-8 flex flex-col justify-between gap-6 rounded-xl border border-violet-500/20 bg-violet-500/[0.04] p-6 lg:flex-row lg:items-center">
          <div className="max-w-2xl">
            <h3 className="text-lg font-semibold text-gray-100">{t('landing.customTitle')}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-400">{t('landing.customDescription')}</p>
            <p className="mt-3 text-sm leading-relaxed text-gray-300">{t('landing.exilProof')} {t('landing.exilDescription')}</p>
          </div>
          <a href={exilSteamUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary shrink-0 border-violet-500/30 text-violet-200 hover:border-violet-400/50">
            {t('landing.wishlist')}<ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>

      <section id="contact" className="border-y border-dark-700 bg-dark-900" aria-labelledby="contact-title">
        <div className="mx-auto flex max-w-6xl flex-col justify-between gap-7 px-6 py-12 sm:py-16 lg:flex-row lg:items-center">
          <div><h2 id="contact-title" className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('landing.contactTitle')}</h2><p className="mt-3 max-w-xl text-sm leading-relaxed text-gray-400">{t('landing.contactDescription')}</p></div>
          <a href={`mailto:${contactEmail}`} className="group inline-flex w-fit items-center gap-3 border-b border-neon-500/40 pb-2 text-xl font-medium text-neon-400 transition-colors hover:text-neon-300 focus-visible:outline focus-visible:outline-neon-400 sm:text-2xl">
            {contactEmail}<ArrowUpRight className="h-5 w-5 shrink-0 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 motion-reduce:transform-none" aria-hidden="true" />
          </a>
        </div>
      </section>
      <footer className="mx-auto flex max-w-6xl flex-col justify-between gap-2 px-6 py-6 text-xs text-gray-500 sm:flex-row">
        <span>© {new Date().getFullYear()} Hostinator</span><span>{t('landing.footer')}</span>
      </footer>
    </div>
  );
}
